import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppConfigService } from '@core/appconfig.service';
import { ApiResponse } from '@models/api-response';
import { AppEnvironments } from '@models/app-environments';
import { IFileShareContent } from '@models/content/file-share-content';
import { IGoogleDriveContent } from '@models/content/google-drive-content';
import { GoogleDriveTypes } from '@models/content/google-drive-types';
import { KeyValuePair } from '@models/key-value-pair';
import { IProductLine, IProductLineConfiguration } from '@models/product-line';
import { ProductDownload } from '@shared/enums/product-download';
import { Helpers } from '@shared/helpers';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AdminProductsApiService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
  ) { }

  // Product-related endpoints
  private getProductSpreadsheetUrl(): string {
    return `${this.appConfig.apiUrl}/product/spreadsheet`;
  }

  // Content-related endpoints
  private getGoogleContentUrl(folderId: string = null, contentId: string = null): string {
    const baseUrl = `${this.appConfig.apiUrl}/google/content/sheet`;
    return folderId || contentId ? `${baseUrl}?folderId=${folderId ?? ''}&contentId=${contentId ?? ''}` : baseUrl;
  }

  // Asset-related endpoints
  private getProductAssetUrl(assetPath: string = null): string {
    const baseUrl = `${this.appConfig.apiUrl}/product/assets`;
    return assetPath ? `${baseUrl}?assetPath=${assetPath ?? ''}` : baseUrl;
  }

  // Product Service Methods
  updateProducts(formData: FormData): Observable<ApiResponse<boolean>> {
    const headers = new HttpHeaders({ 'Content-Type': 'multipart/form-data' });
    return this.http.post(this.getProductSpreadsheetUrl(), formData, { headers, responseType: 'json' })
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] }))
      );
  }

  getProducts(productKey: ProductDownload): Observable<Blob> {
    const params = new HttpParams({ fromObject: { productKey } });
    return this.http.get(this.getProductSpreadsheetUrl(), { params, responseType: 'blob' });
  }

  getProductLineAnnouncements(params: KeyValuePair[] = []): Observable<ApiResponse<IProductLine[]>> {
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, '/product-line-announcement', params);
    return this.http.get(url)
      .pipe(
        map(res => new ApiResponse<IProductLine[]>(true, res)),
      );
  }

  getProductLinesForAnnouncement(announcementId: string): Observable<ApiResponse<IProductLine[]>> {
    return this.getProductLineAnnouncements([{ key: 'announcementId', value: announcementId }]);
  }

  getProductLines(params: KeyValuePair[] = []): Observable<ApiResponse<IProductLineConfiguration[]>> {
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, '/product-line', params);
    return this.http.get(url)
      .pipe(
        map(res => new ApiResponse<IProductLineConfiguration[]>(true, res)),
      );
  }

  updateProductsFromGoogleDrive(): Observable<ApiResponse<boolean>> {
    const url = `${this.appConfig.apiUrl}/google/product/sheet`;
    return this.http.post<ApiResponse<void>>(url, {})
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] }))
      );
  }

  // Content Service Methods
  getContent(folderId: string = null, contentSheetPath: string = null): Observable<ApiResponse<IGoogleDriveContent[]>> {
    return this.http.get(this.getGoogleContentUrl(folderId, contentSheetPath))
      .pipe(
        map((res: ApiResponse<IGoogleDriveContent[]>) => new ApiResponse<IGoogleDriveContent[]>(true, {
          response: res.response,
          messages: [...res.messages],
        })),
      );
  }

  ingestContent(contentId: string, environment: string = null, type: GoogleDriveTypes = null): Observable<ApiResponse<boolean>> {
    const request = {
      contentId,
      environment: environment === AppEnvironments.development ? 'AzureDevelopment' : environment,
      type,
    };
    return this.http.post<ApiResponse<void>>(`${this.getGoogleContentUrl()}`, request)
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] }))
      );
  }

  // Asset Service Methods
  getAssets(assetPath: string = null): Observable<ApiResponse<IFileShareContent[]>> {
    return this.http.get(this.getProductAssetUrl(assetPath))
      .pipe(
        map((res: ApiResponse<IFileShareContent[]>) => new ApiResponse<IFileShareContent[]>(true, {
          response: res.response,
          messages: [...res.messages],
        })),
      );
  }

  ingestAssets(filePath: string, environment: string, isDirectory: boolean): Observable<ApiResponse<boolean>> {
    const request = {
      filePath,
      environment: environment === AppEnvironments.development ? 'AzureDevelopment' : environment,
      isDirectory,
    };
    return this.http.post<ApiResponse<void>>(this.getProductAssetUrl(), request)
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] }))
      );
  }
}
